import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault, isMobileBreakpoint, prefersReducedMotion } from './utilities.js';
import { Component } from '@theme/component';
import { ZoomChangeEvent } from '@theme/events';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DRAG_THRESHOLD = 10;
// AP: one click or tap toggles between 1x and this level (overridable per block via
// `data-zoom-level` on the enclosing <zoom-dialog>). 2x since 8 Oct 2026: with 3200px
// masters, 3x on a 5K display was a 2x upscale of the master's pixels.
const CLICK_ZOOM_DEFAULT = 2;
// AP: a click zooms to at least the scale that makes the media cover its clip frame, times
// this. The 2% overshoot hides the hairline the dialog scrollbar leaves behind: the frame is
// measured before `[data-zoomed]` hides the scrollbar, so it is a scrollbar narrower than the
// frame the zoomed picture actually has to fill.
const COVER_OVERSHOOT = 1.02;
// AP: a tap that lasts longer than this is a press, not a toggle
const TAP_MAX_DURATION = 300;
// AP: on touch, zoom toggles on a double-tap (two still taps this close in time and space), so
// a stray single tap mid-swipe never zooms. Mouse and keyboard toggle on a single activation.
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
// AP: matches the 250ms transform transition in product-media-gallery-content.liquid, plus slack
const TOGGLE_ANIMATION_MS = 300;
// AP: viewport pixels moved per arrow-key press while zoomed
const KEY_PAN_STEP = 40;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} [image] - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  // AP: click-to-zoom state (desktop pointer path + tap toggle)
  /** @type {boolean} */
  #tapCandidate = false;
  /** @type {number} */
  #touchStartTime = 0;
  /** @type {number} */
  #animationTimer = 0;
  /** @type {boolean} */
  #zoomed = false;
  /** @type {string} */
  #lastPointerType = 'mouse';
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  get #image() {
    // The video is cloned in lazily, so this must be queried at use time
    return this.refs.image ?? this.querySelector('video');
  }

  /**
   * AP: the element whose box the zoomed media is clipped to. On desktop that is the slide
   * (full viewport width), not this wrapper (the contain-sized media box), so panning stops
   * at the picture's edge rather than leaving a blank strip inside the slide.
   */
  get #clipFrame() {
    return this.closest('.product-media-container') ?? this;
  }

  /** AP: the click/tap zoom level configured on the enclosing zoom dialog. */
  get #clickZoomLevel() {
    const configured = parseFloat(this.closest('zoom-dialog')?.getAttribute('data-zoom-level') ?? '');
    const level = Number.isFinite(configured) ? configured : CLICK_ZOOM_DEFAULT;

    return clamp(level, MIN_ZOOM + 0.5, MAX_ZOOM);
  }

  /**
   * AP: the scale a click actually zooms to, the largest of three candidates:
   *
   * 1. The configured level (a floor, never the target by itself).
   * 2. Cover: far enough for the drawn media to cover the clip frame in both directions, so a
   *    height-fitted portrait (1080 of 2560 CSS px on a 5K display) never sits between white
   *    columns.
   * 3. Real size, fine pointers only: one master pixel per CSS pixel, the master's width over
   *    its drawn width (the slide carries `data-master-width`). Every screen then sees all of
   *    the master's detail and none stretches it further than a standard monitor at 100%:
   *    about 3x on a 5K display, 4x+ on a laptop. Touch keeps the configured level for its
   *    double-tap (a phone would otherwise jump straight to the pinch ceiling) and pinches on.
   *
   * Capped at MAX_ZOOM. Pinch is unaffected.
   */
  #clickZoomTarget() {
    const wrapperRect = this.getBoundingClientRect();
    const frameRect = this.#clipFrame.getBoundingClientRect();
    const drawn = this.#drawnMediaSize(wrapperRect);

    let cover = MIN_ZOOM;
    if (drawn.width > 0 && drawn.height > 0 && frameRect.width > 0 && frameRect.height > 0) {
      cover = Math.max(frameRect.width / drawn.width, frameRect.height / drawn.height) * COVER_OVERSHOOT;
    }

    let realSize = MIN_ZOOM;
    const masterWidth = parseFloat(this.#clipFrame.dataset.masterWidth ?? '');
    if (this.#lastPointerType !== 'touch' && Number.isFinite(masterWidth) && masterWidth > 0 && drawn.width > 0) {
      realSize = masterWidth / drawn.width;
    }

    return clamp(Math.max(this.#clickZoomLevel, cover, realSize), MIN_ZOOM + 0.5, MAX_ZOOM);
  }

  connectedCallback() {
    super.connectedCallback();

    // Fresh controller per connect: an aborted AbortController can't be reused, so if this
    // element is ever detached and reconnected the touch listeners must re-bind against a
    // new signal. Pairs with #initialized reset in disconnectedCallback.
    this.#controller = new AbortController();

    // AP: the gallery morphs on variant change; a reconnected wrapper must start at 1x
    this.#resetZoom();

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    // AP: click/tap toggle, cursor panning and keyboard panning on every breakpoint
    this.#initPointerListeners();

    if (!isMobileBreakpoint()) return;

    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Initialize transform immediately
    this.#updateTransform();
  }

  /**
   * AP: non-touch pointer listeners (mouse, pen, trackpad). Touch stays on the touch handlers
   * above so pinch and swipe-paging are untouched. Zara-style: while zoomed the picture follows
   * the cursor, so there is no drag-pan on fine pointers (the touch drag remains). The
   * pointermove is bound on the slide, not this wrapper, so the whole viewport-wide frame
   * steers the picture; the controller's signal unbinds it with everything else.
   */
  #initPointerListeners() {
    const { signal } = this.#controller;

    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.#clipFrame.addEventListener('pointermove', this.#handleHoverPan, { signal });
    // A mouse drag over an <img> would otherwise start a native image drag
    this.addEventListener('dragstart', preventDefault, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#initialized = false; // allow touch listeners to re-bind on reconnect
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
    this.#stopAnimation();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) {
      this.#initEventListeners();
    }

    // AP: re-clamp a zoomed image when the frame changes size, on every breakpoint
    if (this.#initialized || this.#scale > MIN_ZOOM) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * AP: toggles between 1x and the configured click zoom level. Wired declaratively from the
   * zoom toggle button (`on:click="/toggleZoom"`), so it also fires for Enter/Space.
   *
   * @param {MouseEvent} event
   */
  toggleZoom(event) {
    // Keyboard activation reports detail 0 and no usable coordinates: anchor at the centre
    const isKeyboard = !event || event.detail === 0;
    const point = isKeyboard ? undefined : { x: event.clientX, y: event.clientY };

    // Touch toggles on a double-tap only. (While zoomed, touch taps never reach here on the
    // mobile breakpoint because the touch handlers preventDefault; #handleTouchEnd covers them.)
    if (point && this.#lastPointerType === 'touch' && !this.#registerTap(point)) return;

    if (this.#scale > MIN_ZOOM) {
      this.#zoomTo(MIN_ZOOM);
      return;
    }

    this.#zoomTo(this.#clickZoomTarget(), point);
  }

  /**
   * AP: records a still tap and reports whether it completed a double-tap. The memory clears
   * after a double-tap (so a third tap starts over), after a pan, a pinch, and on reset.
   * @param {Point} point
   * @returns {boolean}
   */
  #registerTap(point) {
    const now = performance.now();
    const previous = this.#lastTapPosition;
    const isDoubleTap =
      previous !== null &&
      now - this.#lastTapTime < DOUBLE_TAP_DELAY &&
      getDistance(point, previous) < DOUBLE_TAP_DISTANCE;

    if (isDoubleTap) {
      this.#clearTapMemory();
      return true;
    }

    this.#lastTapTime = now;
    this.#lastTapPosition = { x: point.x, y: point.y };
    return false;
  }

  #clearTapMemory() {
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
  }

  /**
   * AP: animate to a scale, keeping `point` (viewport coordinates) stationary when given.
   * @param {number} targetScale
   * @param {Point} [point]
   */
  #zoomTo(targetScale, point) {
    const oldScale = this.#scale;
    this.#scale = clamp(targetScale, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate = { x: 0, y: 0 };
    } else if (point && this.#lastPointerType !== 'touch') {
      // AP: cursor panning takes over from here, so land where the cursor map says
      this.#followPointer(point);
    } else if (point) {
      // Same correction as the pinch midpoint maths in #processZoomGesture, measured from the
      // wrapper's rendered centre (the image's transform origin) rather than its client size,
      // so it is right when the wrapper is not at the viewport origin (desktop dialog).
      const rect = this.getBoundingClientRect();
      const distanceFromCenterX = point.x - (rect.left + rect.width / 2);
      const distanceFromCenterY = point.y - (rect.top + rect.height / 2);
      const scaleDelta = this.#scale / oldScale - 1.0;

      this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;
    }

    this.#isDragging = false;
    this.#startAnimation();
    // Synchronous so the transition starts from the current frame
    this.#cancelAnimationFrame();
    this.#updateTransform();
  }

  /**
   * Remembered for the click that follows, so toggleZoom knows a touch needs a double-tap and
   * the zoom target knows whether real size applies.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    this.#lastPointerType = event.pointerType;
  };

  /** AP: Zara-style cursor panning is on whenever a fine pointer has a zoomed picture. */
  get #cursorFollows() {
    return this.#scale > MIN_ZOOM && this.#lastPointerType !== 'touch';
  }

  /**
   * AP: cursor panning. Moving the mouse over the slide pans the zoomed picture; no press or
   * drag needed (ysl.com drags, zara.com follows the cursor; Josiah chose Zara's, 9 Oct 2026).
   * @param {PointerEvent} event
   */
  #handleHoverPan = (event) => {
    if (event.pointerType === 'touch' || !this.#cursorFollows) return;

    this.#followPointer({ x: event.clientX, y: event.clientY });
    this.#requestUpdateTransform();
  };

  /**
   * AP: maps the cursor's position inside the slide to the picture's position inside its
   * overflow: the slide's top edge shows the top of the picture, its bottom edge the bottom, and
   * the same across for whatever horizontal overflow the zoom leaves. For a height-fitted image
   * this is exactly where the old click anchor landed, so the zoom-in animation and the first
   * mouse move agree.
   * @param {Point} point - viewport coordinates
   */
  #followPointer(point) {
    const frameRect = this.#clipFrame.getBoundingClientRect();
    if (!frameRect.width || !frameRect.height) return;

    const drawn = this.#drawnMediaSize(this.getBoundingClientRect());
    const overflowX = Math.max(0, drawn.width * this.#scale - frameRect.width);
    const overflowY = Math.max(0, drawn.height * this.#scale - frameRect.height);
    const u = clamp((point.x - frameRect.left) / frameRect.width, 0, 1);
    const v = clamp((point.y - frameRect.top) / frameRect.height, 0, 1);

    // translate() runs inside scale(), so viewport pixels divide by the scale
    this.#translate.x = ((0.5 - u) * overflowX) / this.#scale;
    this.#translate.y = ((0.5 - v) * overflowY) / this.#scale;
  }

  /**
   * AP: arrow keys pan a zoomed image so keyboard users can reach every part of it.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (this.#scale <= MIN_ZOOM) return;

    const step = KEY_PAN_STEP / this.#scale;
    let dx = 0;
    let dy = 0;

    switch (event.key) {
      case 'ArrowLeft':
        dx = step;
        break;
      case 'ArrowRight':
        dx = -step;
        break;
      case 'ArrowUp':
        dy = step;
        break;
      case 'ArrowDown':
        dy = -step;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#translate.x += dx;
    this.#translate.y += dy;
    this.#startAnimation();
    this.#requestUpdateTransform();
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    // Leave touches on the play/pause toggle to the button, even while zoomed in
    if (event.target instanceof Element && event.target.closest('.video-interaction-hint')) return;

    // At 1x there is nothing to pan — leave single touches to the scroll container
    // so swiping navigates between media. Pinches and zoomed-in pans are ours.
    if (event.touches.length === 2 || this.#scale > MIN_ZOOM) {
      preventDefault(event);
    }

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      this.#tapCandidate = false;
      this.#clearTapMemory();

      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      // Avoid object allocation by passing touches directly
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      // AP: preventDefault above means no `click` follows a touch while zoomed, so taps are
      // detected here instead and a double-tap toggles back to 1x on touchend.
      this.#tapCandidate = this.#scale > MIN_ZOOM;
      this.#touchStartTime = performance.now();

      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    // Calculate initial distance between touches
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
    this.#stopAnimation();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      this.#tapCandidate = false;
      preventDefault(event);
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging && this.#scale > MIN_ZOOM) {
      preventDefault(event);
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Calculate current distance between touches
    const currentDistance = getDistance(touch1, touch2);

    const oldScale = this.#scale;

    // Calculate and apply new scale
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    // Adjust translation to keep the pinch midpoint stationary
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;

    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;

    // Calculate how the image position needs to change to keep the midpoint stationary
    const scaleDelta = this.#scale / oldScale - 1.0;

    // Apply correction to prevent zooming on the opposite side of the midpoint
    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    // Check if we've moved beyond the drag threshold
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;
    this.#tapCandidate = false;
    this.#clearTapMemory();

    // Calculate movement deltas for translation
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    // Calculate new translation directly
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;

      // AP: a quick, still double-tap on a zoomed image zooms back out
      const wasTap =
        this.#tapCandidate &&
        !this.#hasDraggedBeyondThreshold &&
        performance.now() - this.#touchStartTime < TAP_MAX_DURATION;

      this.#tapCandidate = false;
      this.#hasDraggedBeyondThreshold = false;

      if (wasTap && this.#scale > MIN_ZOOM && this.#registerTap(this.#startPosition)) {
        this.#zoomTo(MIN_ZOOM);
        return;
      }

      this.#requestUpdateTransform();
    }
  };

  /**
   * AP: the size the media is drawn at inside the wrapper at 1x (object-fit: contain), from
   * its natural (or video) dimensions. Shared by the pan clamp and the click zoom target.
   * @param {DOMRect} wrapperRect
   * @returns {{ width: number, height: number }}
   */
  #drawnMediaSize(wrapperRect) {
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    // Try to get natural dimensions
    if (imageElement instanceof HTMLVideoElement && imageElement.videoWidth > 0 && imageElement.videoHeight > 0) {
      naturalWidth = imageElement.videoWidth;
      naturalHeight = imageElement.videoHeight;
    } else if (imageElement instanceof HTMLImageElement && imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      // AP: not loaded yet (a quick click on a lazy slide). Upstream assumed a square here, which
      // made the zoom-to-fill target wrong for every portrait. The slide's `--media-preview-ratio`
      // is the media's own ratio (the desktop box is sized from it), so use that; square last.
      const ratio = parseFloat(getComputedStyle(this.#clipFrame).getPropertyValue('--media-preview-ratio'));
      naturalWidth = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
      naturalHeight = 1;
    }

    // Calculate how the image fits within the wrapper (object-fit: contain behavior)
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider - width fits exactly, height is smaller (letterboxed top/bottom)
      return { width: wrapperRect.width, height: wrapperRect.width / imageAspectRatio };
    }

    // Image is taller - height fits exactly, width is smaller (letterboxed left/right)
    return { width: wrapperRect.height * imageAspectRatio, height: wrapperRect.height };
  }

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    // Get wrapper dimensions
    const wrapperRect = this.getBoundingClientRect();

    // Calculate ACTUAL media content dimensions at current zoom
    // The element may fill the wrapper, but the content has its own aspect ratio
    const { width: actualImageWidth, height: actualImageHeight } = this.#drawnMediaSize(wrapperRect);

    // Apply current zoom scale
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    // AP: constrain against the clipping frame (the slide), which on desktop is wider than the
    // wrapper. The wrapper is centred in the frame, so the constraint stays symmetric.
    const frameRect = this.#clipFrame.getBoundingClientRect();
    const frameWidth = frameRect.width || wrapperRect.width;
    const frameHeight = frameRect.height || wrapperRect.height;

    // SIMPLE APPROACH: Calculate constraints directly from image content dimensions
    // If image content is larger than the frame, calculate max translation directly

    const horizontalOverflow = Math.max(0, scaledImageWidth - frameWidth);
    const verticalOverflow = Math.max(0, scaledImageHeight - frameHeight);

    // Max translation is half the overflow (since image starts centered)
    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    // Apply symmetric constraints (object-fit: contain behavior)
    // Image starts centered, can move maxTranslate in each direction
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    // Apply final transforms to CSS
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    this.#syncZoomedState();
  };

  /**
   * AP: reflects "zoomed past 1x" as `data-zoomed` on this element, `aria-pressed` on the zoom
   * toggle button, and a bubbling ZoomChangeEvent for the dialog (rail fade, scroll lock).
   * Pinch, click, tap and keyboard all funnel through here, so the state is always truthful.
   */
  #syncZoomedState() {
    const zoomed = this.#scale > MIN_ZOOM;
    if (zoomed === this.#zoomed) return;

    this.#zoomed = zoomed;
    this.toggleAttribute('data-zoomed', zoomed);
    this.querySelector('.product-media__zoom-toggle')?.setAttribute('aria-pressed', String(zoomed));
    this.dispatchEvent(new ZoomChangeEvent(zoomed));
  }

  /** AP: lets the CSS transition run for a toggle or a key press, but never during a drag. */
  #startAnimation() {
    if (prefersReducedMotion()) return;

    this.classList.add('is-animating');
    window.clearTimeout(this.#animationTimer);
    this.#animationTimer = window.setTimeout(() => this.classList.remove('is-animating'), TOGGLE_ANIMATION_MS);
  }

  #stopAnimation() {
    window.clearTimeout(this.#animationTimer);
    this.classList.remove('is-animating');
  }

  /**
   * Reset zoom to default state (1x scale, centered position)
   * Called when zoom is exited/closed
   */
  #resetZoom = () => {
    // Reset scale and translation to defaults
    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
    this.#tapCandidate = false;
    this.#clearTapMemory();
    this.#stopAnimation();

    // Update CSS properties to reflect reset state
    this.style.setProperty('--drag-zoom-scale', MIN_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
    this.#syncZoomedState();
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
    this.#stopAnimation();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
